/**
 * [LGP 2026-10-15, user] NEW FILE — Lead Generator Performance → NAL Leads: the Apps Script page "NAL Leads" (Index.html
 * data-page="nal-records"; JavaScript.html N_COLS, loadNal, nSortBy, renderNal). Every lead marked NAL (N = "NAL" or AB =
 * "NAL Accepted") whose Assigned Date is in the range, oldest first; search over every column; header sort (the two date
 * columns sort by the date itself and start newest-first; text starts A→Z).
 * [LGP-CYCLE 2026-10-27, user] the Team Sameer Report's dates: the 21st-to-20th work month that holds today, a picked date loads at
 * once (no Apply button), Start max = End, End min = Start, End up to the end of next month.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from '../../components/DataTable';
import SingleDateCalendar from '../../components/SingleDateCalendar';
import { fetchLgpNal, LgpNalReport, LgpNalRow } from '../../services/api';
import { Column, SortConfig } from '../../types';
import {
  ViewWrap, ViewBar, Field, Toolbar, SearchInput, StatusLine, TableScroll, Muted, Chip, CellLink,
  MIN_DATE, errorText, textCompare, defaultRange, lastDayOffered, ViewProps,
} from './lgpShared';

/** [JavaScript.html N_COLS] Column order A, D, F, H, I, K, N/AB, O, Q, R, S; sortAs = the raw value sorted on. */
type NKey = 'a' | 'email' | 'website' | 'gen' | 'biz' | 'assignee' | 'status' | 'o' | 'name' | 'team' | 'source';
const N_COLS: { key: NKey; label: string; sortAs?: 'aDay' | 'day'; link?: true; chip?: true }[] = [
  { key: 'a', label: 'Date', sortAs: 'aDay' },
  { key: 'email', label: 'Email Address' },
  { key: 'website', label: 'Website', link: true },
  { key: 'gen', label: 'Lead Generated By' },
  { key: 'biz', label: 'Business Type' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'status', label: 'NAL Status', chip: true },
  { key: 'o', label: 'Assigned Date', sortAs: 'day' },
  { key: 'name', label: 'Lead Generated By Name' },
  { key: 'team', label: 'Team Name' },
  { key: 'source', label: 'Lead Source' },
];

const NalView: React.FC<ViewProps> = ({ active, noDock, storageKey, onVersion, register }) => {
  const defaults = useMemo(() => defaultRange(), []);
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [report, setReport] = useState<LgpNalReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>({ text: 'Reading data…' });
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<NKey>('o');
  const [sortAsc, setSortAsc] = useState(true);              // Assigned Date (O), oldest first
  const req = useRef(0);
  const ref = useRef(report);
  ref.current = report;

  /** [JavaScript.html loadNal] */
  const load = useCallback((silent: boolean, s?: string, e?: string) => {
    const from = s ?? ref.current?.start ?? start;
    const to = e ?? ref.current?.end ?? end;
    if (!from || !to) { setNote({ text: 'Pick both dates first.', error: true }); return; }
    if (!silent) { setBusy(true); setNote({ text: 'Reading data…' }); setReport(null); }
    const n = ++req.current;
    fetchLgpNal(from, to)
      .then((data) => {
        if (n !== req.current) return;
        setReport(data);
        setBusy(false);
        setNote(null);
        onVersion(data.meta.version, 'nal-records');
      })
      .catch((err) => {
        if (n !== req.current || silent) return;
        setBusy(false);
        setNote({ text: errorText(err), error: true });
      });
  }, [start, end, onVersion]);
  const loadRef = useRef(load);
  loadRef.current = load;
  // [LGP-CYCLE 2026-10-27] a picked date loads its records at once (no Apply)
  const lastDay = useMemo(() => lastDayOffered(), []);
  const pickStart = (d: string) => { if (d === start) return; setStart(d); load(false, d, end); };
  const pickEnd = (d: string) => { if (d === end) return; setEnd(d); load(false, start, d); };

  const inited = useRef(false);
  useEffect(() => {
    if (!active || inited.current) return;
    inited.current = true;
    loadRef.current(false);
    register('nal-records', { refresh: () => loadRef.current(true) });
  }, [active, register]);

  /** [JavaScript.html nSortBy] The same header turns it round; a new one: text ascends, dates start newest-first. */
  const onSort = useCallback((key: string) => {
    const k = key as NKey;
    if (k === sortKey) setSortAsc((a) => !a);
    else {
      setSortKey(k);
      const col = N_COLS.find((c) => c.key === k);
      setSortAsc(!(col && col.sortAs));
    }
  }, [sortKey]);

  /** [JavaScript.html renderNal] */
  const rows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    const list = report.rows.filter((r) => !q || N_COLS.some((c) => String(r[c.key]).toLowerCase().includes(q)));
    const col = N_COLS.find((c) => c.key === sortKey);
    const raw = ((col && col.sortAs) || sortKey) as keyof LgpNalRow;
    return list.slice().sort((a, b) => {
      const x = a[raw]; const y = b[raw];
      let cmp = (typeof x === 'number' && typeof y === 'number') ? x - y : textCompare(String(x), String(y));
      if (!cmp) cmp = a.day - b.day;
      return sortAsc ? cmp : -cmp;
    }).map((r, i) => ({ ...r, id: `${r.day}:${r.aDay}:${r.email}:${r.gen}:${i}` }));
  }, [report, search, sortKey, sortAsc]);

  const columns: Column[] = useMemo(() => N_COLS.map((c) => ({
    key: c.key,
    label: c.label,
    sortable: true,
    render: (v: string) => {
      const s = String(v || '');
      if (c.link && s) {
        const href = /^https?:\/\//i.test(s) ? s : `https://${s}`;
        return <CellLink href={href} target="_blank" rel="noopener noreferrer">{s}</CellLink>;
      }
      if (c.chip && s) return <Chip>{s}</Chip>;
      return s || <Muted>—</Muted>;
    },
  })), []);
  const sort: SortConfig = { sortBy: sortKey, sortOrder: sortAsc ? 'asc' : 'desc' };
  const status = note ? note : report ? {
    text: `${report.meta.records.toLocaleString('en-US')} ${report.meta.records === 1 ? 'NAL record' : 'NAL records'} · ${report.heading} · data as of ${report.meta.dataAsOf}`,
  } : { text: '' };
  const emptyTitle = !report ? (note?.error ? note.text : 'No NAL records in this date range.')
    : report.rows.length ? 'No records match that filter.' : 'No NAL records in this date range.';

  return (
    <ViewWrap data-lgp-view="nal-records">
      <ViewBar>
        <span className="sub">NAL leads · rows marked NAL or NAL Accepted, by assigned date</span>
        <div className="controls">
          <Field data-date="start"><span>Start date</span>
            <SingleDateCalendar value={start} min={MIN_DATE} max={end} onChange={pickStart} hint={false} liquid /* [LIQUID-GLASS 2026-11-02] */ /></Field>
          <Field data-date="end"><span>End date</span>
            <SingleDateCalendar value={end} min={start} max={lastDay} onChange={pickEnd} hint={false} liquid /></Field>
        </div>
      </ViewBar>
      <Toolbar>
        <div className="left">
          <SearchInput type="search" placeholder="Filter records" value={search} disabled={busy || !report} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <StatusLine $error={!!status.error} data-status="">{status.text}</StatusLine>
      </Toolbar>
      <TableScroll $noDock={noDock}>
        <DataTable
          columns={columns}
          data={rows}
          sort={sort}
          onSort={onSort}
          loading={busy && !report}
          disableMobileCards
          parentScroll
          resizeStorageKey={`dt-lgp-nal-colw:${storageKey}`}
          empty={{ title: emptyTitle }}
        />
      </TableScroll>
    </ViewWrap>
  );
};

export default NalView;
